import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AndroidDeviceService {
  private readonly STORAGE_KEY_PERMANENT = 'android_app_banner_dismissed';
  private readonly STORAGE_KEY_LATER = 'android_app_banner_later';
  private readonly LATER_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days
  private readonly PLAY_STORE_URL =
    'https://play.google.com/store/apps/details?id=com.the_invoices.thi_mobile_app';

  private _isAndroidDevice$ = new BehaviorSubject<boolean>(false);
  private _showBanner$ = new BehaviorSubject<boolean>(false);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.detectAndroidDevice();
      this.checkBannerVisibility();
    }
  }

  private detectAndroidDevice(): void {
    const ua = (navigator.userAgent || navigator.vendor || (window as any).opera || '').toLowerCase();
    // navigator.platform stays as the real host OS ("MacIntel", "Win32", "Linux x86_64")
    // even when Chrome DevTools mobile emulation is active, so this reliably filters out desktops.
    const platform = (navigator.platform || '').toLowerCase();
    const isDesktop = /mac|win|linux x86/.test(platform);
    const isAndroid = /android/.test(ua) && !isDesktop;
    this._isAndroidDevice$.next(isAndroid);
  }

  /**
   * Show only after login on dashboard routes, only on Android, not for accountants (300/301).
   */
  private checkBannerVisibility(): void {
    const isPermanentlyDismissed =
      localStorage.getItem(this.STORAGE_KEY_PERMANENT) === 'true';
    const isAndroid = this._isAndroidDevice$.value;
    const token = localStorage.getItem('token');
    const isLoggedIn = !!token;

    const userTypeRaw = localStorage.getItem('user_type');
    const userType = userTypeRaw ? parseInt(userTypeRaw, 10) : null;
    const isAccountant = userType === 300 || userType === 301;

    if (!isAndroid || !isLoggedIn || isPermanentlyDismissed || isAccountant || this.isOnAuthRoute()) {
      this._showBanner$.next(false);
      return;
    }

    const laterTimestamp = localStorage.getItem(this.STORAGE_KEY_LATER);
    if (laterTimestamp) {
      const timePassed = Date.now() - parseInt(laterTimestamp, 10);
      if (timePassed < this.LATER_DURATION) {
        this._showBanner$.next(false);
        return;
      }
      localStorage.removeItem(this.STORAGE_KEY_LATER);
    }

    this._showBanner$.next(true);
  }

  get isAndroidDevice$(): Observable<boolean> {
    return this._isAndroidDevice$.asObservable();
  }

  get showBanner$(): Observable<boolean> {
    return this._showBanner$.asObservable();
  }

  get playStoreUrl(): string {
    return this.PLAY_STORE_URL;
  }

  dismissBannerPermanently(): void {
    localStorage.setItem(this.STORAGE_KEY_PERMANENT, 'true');
    localStorage.removeItem(this.STORAGE_KEY_LATER);
    this._showBanner$.next(false);
  }

  dismissBannerLater(): void {
    localStorage.setItem(this.STORAGE_KEY_LATER, Date.now().toString());
    this._showBanner$.next(false);
  }

  recheckBannerVisibility(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.checkBannerVisibility();
    }
  }

  resetDismissal(): void {
    localStorage.removeItem(this.STORAGE_KEY_PERMANENT);
    localStorage.removeItem(this.STORAGE_KEY_LATER);
    this.checkBannerVisibility();
  }

  isAndroidDevice(): boolean {
    return this._isAndroidDevice$.value;
  }

  /** Never show app-download banner on login/OTP/auth screens. */
  private isOnAuthRoute(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const hash = (window.location.hash || '').toLowerCase();
    return /^#\/(login|otp|register|resend-otp|forgot-password)(\/|$|\?)/.test(hash);
  }
}
